import type { Image } from '../state/types';

const MAX_DIMENSION = 2048;
const TARGET_QUALITY = 0.8;
const PER_IMAGE_MAX_SIZE = 500 * 1024;
const MIN_DIMENSION = 512;

export interface ProcessedImage extends Image {
    originalSize: number;
    compressedSize: number;
}

export interface ProcessingResult {
    image: ProcessedImage;
    wasResized: boolean;
    wasCompressed: boolean;
}

export async function processImage(file: File): Promise<ProcessingResult> {
    const originalBuffer = await file.arrayBuffer();
    const originalSize = originalBuffer.byteLength;
    
    const img = await loadImage(file);
    
    let { width, height } = calculateTargetDimensions(img.naturalWidth, img.naturalHeight);
    
    const format = determineOutputFormat(file.type);
    
    let result = await resizeAndCompress(img, width, height, format, TARGET_QUALITY);
    
    if (result.data.length > PER_IMAGE_MAX_SIZE) {
        result = await progressiveReduce(img, width, height, format);
    }
    
    return {
        image: {
            data: result.data,
            media_type: result.mediaType,
            originalSize,
            compressedSize: result.data.length,
        },
        wasResized: width !== img.naturalWidth || height !== img.naturalHeight,
        wasCompressed: true,
    };
}

function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}

function calculateTargetDimensions(origWidth: number, origHeight: number): { width: number; height: number } {
    if (origWidth <= MAX_DIMENSION && origHeight <= MAX_DIMENSION) {
        return { width: origWidth, height: origHeight };
    }
    
    const aspectRatio = origWidth / origHeight;
    
    if (origWidth > origHeight) {
        return {
            width: MAX_DIMENSION,
            height: Math.round(MAX_DIMENSION / aspectRatio),
        };
    } else {
        return {
            width: Math.round(MAX_DIMENSION * aspectRatio),
            height: MAX_DIMENSION,
        };
    }
}

function determineOutputFormat(inputType: string): string {
    const canvas = document.createElement('canvas');
    const supportsWebP = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    
    if (supportsWebP) {
        return 'image/webp';
    }
    
    if (inputType === 'image/png') {
        return 'image/png';
    }
    
    return 'image/jpeg';
}

async function resizeAndCompress(
    img: HTMLImageElement,
    width: number,
    height: number,
    format: string,
    quality: number
): Promise<{ data: string; mediaType: string }> {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get canvas context');
    }
    
    ctx.drawImage(img, 0, 0, width, height);
    
    const dataUrl = canvas.toDataURL(format, quality);
    const base64 = dataUrl.split(',')[1];
    
    return {
        data: base64,
        mediaType: format,
    };
}

async function progressiveReduce(
    img: HTMLImageElement,
    startWidth: number,
    startHeight: number,
    format: string
): Promise<{ data: string; mediaType: string }> {
    let width = startWidth;
    let height = startHeight;
    let quality = TARGET_QUALITY;
    
    let result = await resizeAndCompress(img, width, height, format, quality);
    
    while (result.data.length > PER_IMAGE_MAX_SIZE && quality > 0.1) {
        quality -= 0.1;
        result = await resizeAndCompress(img, width, height, format, quality);
    }
    
    while (result.data.length > PER_IMAGE_MAX_SIZE && width > MIN_DIMENSION && height > MIN_DIMENSION) {
        width = Math.max(MIN_DIMENSION, Math.round(width * 0.8));
        height = Math.max(MIN_DIMENSION, Math.round(height * 0.8));
        result = await resizeAndCompress(img, width, height, format, quality);
    }
    
    return result;
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export function getBase64Size(base64: string): number {
    return Math.ceil(base64.length * 3 / 4);
}